import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError, getStoredToken } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Star, PencilSimple, Trash, Power, PlugsConnected, SpeakerHigh } from "@phosphor-icons/react";

const COPY = {
  models: {
    title: "AI Models",
    subtitle: "Catalog of language models. The first active model becomes the default. New customers receive a snapshot; changing the default does not change existing customers. Live calls are not affected.",
    providerLabel: "Provider",
    nameLabel: "Model",
    identifierLabel: "Model identifier",
    emptyTitle: "No AI models yet",
    emptyBody: "Add the first model. It becomes the default automatically. No production model is seeded.",
    test: "ai-model",
    credentialHelp: "API key for this LLM provider. Leave blank only if the model does not need a key.",
    identifierPlaceholder: "e.g. gemini-2.5-flash, gpt-4o",
  },
  tts: {
    title: "TTS",
    subtitle: "Text-to-speech catalog. Piper is the on-box default. Add a third-party voice here with its API key. Live call audio is unchanged.",
    providerLabel: "Provider",
    nameLabel: "TTS model",
    identifierLabel: "Model identifier",
    emptyTitle: "No TTS configurations",
    emptyBody: "Add a TTS provider and model. Only one configuration can be the default.",
    test: "tts",
    credentialHelp: "Required for cloud TTS. Leave blank for Piper on the AI engine.",
    identifierPlaceholder: "Provider-specific identifier",
  },
  stt: {
    title: "STT",
    subtitle: "Speech-to-text catalog. faster-whisper is the on-box default (no API key). For Deepgram, OpenAI Whisper, Google Speech, or AssemblyAI, add the API key on the record and use Test connection.",
    providerLabel: "Engine",
    nameLabel: "Model",
    identifierLabel: "Model identifier",
    emptyTitle: "No STT configurations",
    emptyBody: "Add a speech-to-text engine. faster-whisper stays the default until an admin chooses another active configuration.",
    test: "stt",
    credentialHelp: "Required for third-party STT (Deepgram, OpenAI Whisper, Google Speech, AssemblyAI). Leave blank for faster-whisper on the AI engine.",
    identifierPlaceholder: "e.g. nova-2, whisper-1, en-US",
  },
};

const EMPTY = {
  provider: "", credential: "", name: "", identifier: "", description: "",
  status: "active", is_default: false, billing_type: "paid",
};

const MENU_KEY = { models: "ai-bots-models", tts: "ai-bots-tts", stt: "ai-bots-stt" };

export default function AICatalog({ kind }) {
  const { user } = useAuth();
  const menuKey = MENU_KEY[kind] || "ai-bots-models";
  const canAdd = can(user, menuKey, "add");
  const canEdit = can(user, menuKey, "edit");
  const canDelete = can(user, menuKey, "delete");
  const copy = COPY[kind];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [voices, setVoices] = useState([]);
  const [previewVoice, setPreviewVoice] = useState("");
  const [previewText, setPreviewText] = useState("Hello, this is a voice preview.");
  const [previewError, setPreviewError] = useState("");
  const [playing, setPlaying] = useState(false);
  const [hearBusy, setHearBusy] = useState(null);
  const audioCtxRef = useRef(null);
  const previewAbort = useRef(null);
  const hearAutoRan = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai-bots/${kind}`, { params: q.trim() ? { q: q.trim() } : {} });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [kind, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { hearAutoRan.current = false; }, [kind, q]);

  const applyHearResult = (rowId, data) => {
    setRows((prev) => prev.map((r) => (
      r.id !== rowId ? r : {
        ...r,
        live_hear_registered: !!data.registered,
        live_hear_message: data.message || "",
        live_hear_checked_at: data.live_hear_checked_at || new Date().toISOString(),
      }
    )));
  };

  const checkLiveHear = async (row, { silent } = {}) => {
    if (kind !== "models" || !row?.live_hear_applicable) return;
    setHearBusy(row.id);
    try {
      const { data } = await api.post(`/ai-bots/models/${row.id}/live-hear-check`);
      applyHearResult(row.id, data);
      if (!silent) {
        if (data.registered) toast.success(data.message || "Caller speech registered");
        else toast.error(data.message || "Caller speech not registered");
      }
    } catch (e) {
      applyHearResult(row.id, {
        registered: false,
        message: formatApiError(e.response?.data?.detail) || "Hear check failed.",
      });
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Hear check failed.");
    } finally {
      setHearBusy(null);
    }
  };

  const fixLiveHear = async (row) => {
    if (kind !== "models" || !row?.live_hear_applicable) return;
    setHearBusy(row.id);
    try {
      const { data } = await api.post(`/ai-bots/models/${row.id}/live-hear-fix`);
      applyHearResult(row.id, data);
      if (data.registered) toast.success(data.message || "Caller speech fixed");
      else toast.error(data.message || "Fix applied but speech still not registered");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not fix caller speech.");
    } finally {
      setHearBusy(null);
    }
  };

  // Auto-check Gemini models once after load when status is unknown.
  useEffect(() => {
    if (kind !== "models" || loading || hearAutoRan.current) return;
    const pending = rows.filter(
      (r) => r.live_hear_applicable && (r.live_hear_registered === null || r.live_hear_registered === undefined)
    );
    if (!pending.length) return;
    hearAutoRan.current = true;
    (async () => {
      for (const row of pending) {
        await checkLiveHear(row, { silent: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, loading, rows]);

  const currentDefault = rows.find((r) => r.is_default && r.status === "active");

  const openNew = () => {
    setEdit(null);
    setForm({
      ...EMPTY,
      billing_type: (kind === "tts" || kind === "stt") ? "paid" : "paid",
    });
    setOpen(true);
  };
  const openEdit = (row) => {
    setEdit(row);
    setForm({
      provider: row.provider || "",
      credential: "",
      name: row.name || "",
      identifier: row.identifier || "",
      description: row.description || "",
      status: row.status || "active",
      is_default: !!row.is_default,
      billing_type: row.billing_type === "free" ? "free" : "paid",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        provider: form.provider.trim(),
        name: form.name.trim(),
        identifier: form.identifier.trim(),
        description: form.description.trim() || null,
      };
      if (form.credential.trim()) body.credential = form.credential;
      if (kind === "tts" || kind === "stt") body.billing_type = form.billing_type || "paid";
      if (edit) {
        await api.patch(`/ai-bots/${kind}/${edit.id}`, body);
        if (form.status !== edit.status) {
          await api.post(`/ai-bots/${kind}/${edit.id}/status`, { status: form.status });
        }
      } else {
        await api.post(`/ai-bots/${kind}`, {
          ...body,
          status: form.status,
          is_default: form.status === "active" && form.is_default,
        });
      }
      toast.success(edit ? "Saved" : "Added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const run = async (id, fn) => {
    setBusy(id);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const setDefault = (row) => run(row.id, async () => {
    await api.post(`/ai-bots/${kind}/${row.id}/default`);
    toast.success("Default updated");
  });

  const toggleStatus = (row) => run(row.id, async () => {
    const status = row.status === "active" ? "inactive" : "active";
    await api.post(`/ai-bots/${kind}/${row.id}/status`, { status });
    toast.success(status === "active" ? "Enabled" : "Disabled");
  });

  const stopPreviewAudio = () => {
    previewAbort.current?.abort();
    previewAbort.current = null;
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) ctx.close().catch(() => {});
    setPlaying(false);
  };

  const openPreview = async (row) => {
    stopPreviewAudio();
    setPreview(row);
    setVoices([]);
    setPreviewVoice("");
    setPreviewError("");
    setPreviewText("Hello, this is a voice preview.");
    try {
      const { data } = await api.get(`/ai-bots/tts/${row.id}/voices`);
      const list = data.voices || [];
      setVoices(list);
      if (list[0]) setPreviewVoice(list[0].name);
      if (!list.length && data.message) setPreviewError(data.message);
    } catch (e) {
      setPreviewError(formatApiError(e.response?.data?.detail) || "Could not load voices.");
    }
  };

  const playPreview = async () => {
    if (!preview) return;
    stopPreviewAudio();
    setPlaying(true);
    setPreviewError("");
    const ctrl = new AbortController();
    previewAbort.current = ctrl;
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    ctx.resume().catch(() => {});
    let nextAt = 0;
    let pending = "";
    const schedulePcm = (b64, rate) => {
      if (audioCtxRef.current !== ctx) return;
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length - (binary.length % 2));
      for (let i = 0; i < bytes.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const samples = new Int16Array(bytes.buffer);
      if (!samples.length) return;
      const buffer = ctx.createBuffer(1, samples.length, rate || 24000);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < samples.length; i += 1) channel[i] = samples[i] / 32768;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      const start = Math.max(ctx.currentTime + 0.05, nextAt);
      source.start(start);
      nextAt = start + buffer.duration;
    };
    const playWav = (b64, mime) => {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime || "audio/wav" }));
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.play().catch(() => setPreviewError("The browser blocked playback. Press Play again."));
    };
    try {
      const res = await fetch(`${API}/ai-bots/tts/${preview.id}/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getStoredToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: previewText, voice: previewVoice }),
        signal: ctrl.signal,
      });
      const ctype = res.headers.get("content-type") || "";
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiError(body.detail) || "Could not play this voice.");
      }
      if (!ctype.includes("ndjson")) {
        const data = await res.json();
        playWav(data.audio_base64, data.mime);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        pending += decoder.decode(value, { stream: true });
        let nl = pending.indexOf("\n");
        while (nl >= 0) {
          const line = pending.slice(0, nl).trim();
          pending = pending.slice(nl + 1);
          nl = pending.indexOf("\n");
          if (!line) continue;
          const msg = JSON.parse(line);
          if (msg.error) {
            setPreviewError(msg.error);
            setPlaying(false);
            return;
          }
          if (msg.pcm) schedulePcm(msg.pcm, msg.rate);
          if (msg.done) {
            const wait = Math.max(0, (nextAt - ctx.currentTime) * 1000);
            window.setTimeout(() => {
              if (audioCtxRef.current === ctx) setPlaying(false);
            }, wait + 80);
          }
        }
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      setPlaying(false);
      setPreviewError(e?.message || "Could not play this voice.");
    }
  };

  const testConnection = async (row) => {
    setBusy(row.id);
    try {
      const { data } = await api.post(`/ai-bots/${kind}/${row.id}/test`);
      setTestResult({ name: row.name, ok: !!data.ok, message: data.message || "" });
    } catch (e) {
      setTestResult({
        name: row.name,
        ok: false,
        message: formatApiError(e.response?.data?.detail) || "Connection test failed.",
      });
    } finally {
      setBusy(null);
    }
  };

  const remove = (row) => run(row.id, async () => {
    await api.delete(`/ai-bots/${kind}/${row.id}`);
    toast.success("Removed");
    setConfirm(null);
  });

  const canSave = form.provider.trim() && form.name.trim() && form.identifier.trim();

  return (
    <div>
      <PageHeader title={copy.title} subtitle={copy.subtitle}>
        {canAdd && (
        <Button onClick={openNew} data-testid={`${copy.test}-add`} className="rounded-lg">
          <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add
        </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search provider, model, or identifier"
          className="max-w-sm"
          data-testid={`${copy.test}-search`}
        />
        {currentDefault && (
          <p className="text-sm text-muted-foreground" data-testid={`${copy.test}-current-default`}>
            Current default: <span className="font-medium text-foreground">{currentDefault.provider} · {currentDefault.name}</span>
          </p>
        )}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid={`${copy.test}-empty`} icon={Star} title={copy.emptyTitle} description={copy.emptyBody} />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid={`${copy.test}-table`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{copy.providerLabel}</TableHead>
                  <TableHead>{copy.nameLabel}</TableHead>
                  <TableHead>Identifier</TableHead>
                  <TableHead>API key</TableHead>
                  {(kind === "tts" || kind === "stt") && <TableHead>Paid</TableHead>}
                  {kind === "models" && <TableHead>Caller speech</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} data-testid={`${copy.test}-row-${row.id}`}>
                    <TableCell className="font-medium">{row.provider}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="font-mono text-xs">{row.identifier}</TableCell>
                    <TableCell className="font-mono text-sm" data-testid={`${copy.test}-key-${row.id}`}>
                      {row.credential_mask || "Not set"}
                    </TableCell>
                    {(kind === "tts" || kind === "stt") && (
                      <TableCell>
                        <span className={`text-xs font-semibold ${
                          row.billing_type === "free"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-primary"
                        }`}>
                          {row.billing_type === "free" ? "Free" : "Paid"}
                        </span>
                      </TableCell>
                    )}
                    {kind === "models" && (
                      <TableCell>
                        {row.live_hear_applicable ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-md px-1 py-0.5 text-left hover:bg-muted/60 disabled:opacity-60"
                            disabled={hearBusy === row.id}
                            onClick={() => (
                              row.live_hear_registered
                                ? checkLiveHear(row)
                                : fixLiveHear(row)
                            )}
                            title={
                              hearBusy === row.id
                                ? "Checking…"
                                : row.live_hear_registered
                                  ? (row.live_hear_message || "Caller speech registered — click to re-check")
                                  : (row.live_hear_message || "Not registering caller speech — click to fix")
                            }
                            aria-label={
                              row.live_hear_registered
                                ? "Caller speech registered"
                                : "Caller speech not registered — click to fix"
                            }
                            data-testid={`${copy.test}-live-hear-${row.id}`}
                          >
                            <span
                              className={
                                `live-hear-dot ${
                                  hearBusy === row.id
                                    ? "live-hear-dot--busy"
                                    : row.live_hear_registered === true
                                      ? "live-hear-dot--ok"
                                      : row.live_hear_registered === false
                                        ? "live-hear-dot--bad"
                                        : "live-hear-dot--pending"
                                }`
                              }
                            />
                            <span className="text-xs text-muted-foreground">
                              {hearBusy === row.id
                                ? "Checking…"
                                : row.live_hear_registered === true
                                  ? "Registered"
                                  : row.live_hear_registered === false
                                    ? "Not registering"
                                    : "Unknown"}
                            </span>
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell><StatusPill status={row.status} /></TableCell>
                    <TableCell>
                      {row.is_default
                        ? <span className="text-xs font-semibold text-amber-600" data-testid={`${copy.test}-default-${row.id}`}>YES</span>
                        : <span className="text-xs text-muted-foreground">NO</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {kind === "tts" && canEdit && (
                          <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busy === row.id}
                            onClick={() => openPreview(row)} data-testid={`${copy.test}-preview-${row.id}`}
                            title="Preview" aria-label="Preview">
                            <SpeakerHigh className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busy === row.id}
                          onClick={() => testConnection(row)} data-testid={`${copy.test}-test-${row.id}`}
                          title={busy === row.id ? "Testing…" : "Test connection"}
                          aria-label={busy === row.id ? "Testing…" : "Test connection"}>
                          <PlugsConnected className="w-4 h-4" />
                        </Button>
                        )}
                        {canEdit && !row.is_default && row.status === "active" && (
                          <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busy === row.id}
                            onClick={() => setDefault(row)} data-testid={`${copy.test}-makedefault-${row.id}`}
                            title="Set as default" aria-label="Set as default">
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit && (
                        <Button variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0" disabled={busy === row.id}
                          onClick={() => toggleStatus(row)} data-testid={`${copy.test}-status-${row.id}`}
                          title={row.status === "active" ? "Disable" : "Enable"}
                          aria-label={row.status === "active" ? "Disable" : "Enable"}>
                          <Power className="w-4 h-4" />
                        </Button>
                        )}
                        {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openEdit(row)}
                          data-testid={`${copy.test}-edit-${row.id}`} title="Edit" aria-label="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        )}
                        {canDelete && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => setConfirm(row)} data-testid={`${copy.test}-delete-${row.id}`}
                          title="Delete" aria-label="Delete">
                          <Trash className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${copy.test}-dialog`}>
          <DialogHeader>
            <DialogTitle>{edit ? "Edit" : "Add"} {copy.title.replace(/s$/, "")}</DialogTitle>
            <DialogDescription>
              {kind === "stt"
                ? "Enter the third-party STT API key to connect the service. After saving, only a mask is shown. Leave blank to keep the current key, or for on-box faster-whisper."
                : "Add the API key for this service. After saving, only a mask is shown. Leave the field blank to keep the current key. Piper and faster-whisper run on the AI engine and do not need a key."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{copy.providerLabel}</Label>
                <Input data-testid={`${copy.test}-provider`} value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  placeholder={kind === "stt" ? "Deepgram / OpenAI / Google / faster-whisper" : "OpenAI"} />
              </div>
              <div className="space-y-2">
                <Label>{copy.nameLabel}</Label>
                <Input data-testid={`${copy.test}-name`} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>API key</Label>
              <Input data-testid={`${copy.test}-credential`} type="password" value={form.credential}
                autoComplete="new-password"
                onChange={(e) => setForm({ ...form, credential: e.target.value })}
                placeholder={edit?.has_credential ? `${edit.credential_mask} — leave blank to keep` : "API key for this service"} />
              {copy.credentialHelp && (
                <p className="text-[11px] text-muted-foreground">{copy.credentialHelp}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{copy.identifierLabel}</Label>
              <Input data-testid={`${copy.test}-identifier`} value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                placeholder={copy.identifierPlaceholder || "Provider-specific identifier"} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid={`${copy.test}-description`} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="min-h-[80px]" />
            </div>
            {(kind === "tts" || kind === "stt") && (
              <div className="space-y-2">
                <Label>Paid</Label>
                <Select value={form.billing_type || "paid"} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                  <SelectTrigger data-testid={`${copy.test}-billing-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Free shows as “Free / min” on the customer Billing and Pricing pages when this {kind.toUpperCase()} is assigned.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v, is_default: v === "active" ? form.is_default : false })}>
                <SelectTrigger data-testid={`${copy.test}-status-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!edit && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_default} disabled={form.status !== "active"}
                  onCheckedChange={(v) => setForm({ ...form, is_default: !!v })}
                  data-testid={`${copy.test}-default-check`} />
                Set as the only default
              </label>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !canSave} data-testid={`${copy.test}-save`} className="rounded-lg">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) { stopPreviewAudio(); setPreview(null); } }}>
        <DialogContent data-testid="tts-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview {preview?.name}</DialogTitle>
            <DialogDescription>
              {preview?.provider} · {preview?.identifier}. Type a line, choose a voice this model offers, and play it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Text</Label>
              <Textarea data-testid="tts-preview-text" value={previewText} maxLength={4000}
                onChange={(e) => setPreviewText(e.target.value)} className="min-h-[90px]" />
              <p className="text-xs text-muted-foreground">Playback starts on the first sentence. Longer text continues from there.</p>
            </div>
            <div className="space-y-2">
              <Label>Voice</Label>
              <Select value={previewVoice || undefined} onValueChange={setPreviewVoice} disabled={!voices.length}>
                <SelectTrigger data-testid="tts-preview-voice"><SelectValue placeholder={voices.length ? "Select a voice" : "No voices"} /></SelectTrigger>
                <SelectContent>
                  {voices.map((v) => (
                    <SelectItem key={v.name} value={v.name}>{v.style ? `${v.name} — ${v.style}` : v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {previewError && <p className="text-sm text-destructive" data-testid="tts-preview-error">{previewError}</p>}
          </div>
          <DialogFooter>
            <Button onClick={playPreview} disabled={playing || !previewText.trim() || !previewVoice}
              data-testid="tts-preview-play" className="rounded-lg">
              {playing ? "Playing…" : "Play"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!testResult} onOpenChange={(v) => !v && setTestResult(null)}>
        <DialogContent data-testid={`${copy.test}-test-result`}>
          <DialogHeader>
            <DialogTitle>{testResult?.ok ? "Connected" : "Not connected"}</DialogTitle>
            <DialogDescription>
              {testResult?.name}. {testResult?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="rounded-lg" onClick={() => setTestResult(null)} data-testid={`${copy.test}-test-close`}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid={`${copy.test}-confirm`}>
          <DialogHeader>
            <DialogTitle>Remove {confirm?.name}?</DialogTitle>
            <DialogDescription>
              The only active default cannot be removed. If another active configuration exists, it becomes the default.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="rounded-lg" disabled={busy === confirm?.id} onClick={() => remove(confirm)}
              data-testid={`${copy.test}-confirm-delete`}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
